/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useLocation } from "@epic-mw/app-context";
import { useLocale } from "@epic-mw/i18n";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import useSWR from "swr";

import { useAccountContext } from "../account/account.hooks";
import { useApiConfig } from "../api-config";

import { AuthRes, auth } from "./auth.fetch";

export const useAccountLoginRequired = (): { accountLoading: boolean; isLoggedIn?: boolean } => {
	const { account, isLoading } = useAccountContext();
	const router = useRouter();
	const location = useLocation();
	const locale = useLocale();
	const isLoggedIn = account.isLoggedIn;
	if (account && !isLoading && !isLoggedIn) {
		console.info("useAccountLoginRequired() redirecting to /auth");
		router.push(`/${locale}/auth?redirectUrl=${location.toString()}`);
	}
	return { accountLoading: isLoading, isLoggedIn };
};

export const useAuth = (
	disable?: boolean,
): {
	auth?: AuthRes;
	isLoading: boolean;
	error?: string;
} => {
	const { refreshAccount, setAccount } = useAccountContext();
	const { serverUrl } = useApiConfig();
	/*
	  Revalidating on focus is a feature here, not SWR boilerplate. A user who
	  signs in on another epicgames.com tab and switches back must arrive signed
	  in, and the same applies to signing out. Nothing else notices: the session
	  cookie is written by the other tab, and this tab has no reason to re-read
	  it until something asks.

	  So this deliberately does NOT use `swr/immutable`, which sets
	  revalidateOnFocus, revalidateIfStale and revalidateOnReconnect to false. An
	  earlier commit on this branch switched to it, on the reasoning that the
	  local name `useSWRImmutable` should match its import. The name was wrong,
	  not the behaviour: `swr` has no named `useSWRImmutable` export, so that
	  import was plain `useSWR` all along, and "correcting" it silently removed
	  cross-tab sign-in detection. Named honestly now, with the options stated
	  rather than inherited, so the next reader cannot make the same trade.

	  Focus revalidation is throttled by SWR's `focusThrottleInterval`, 5s by
	  default. Left at the default on purpose: a longer window would swallow the
	  exact case this exists for, because signing in on another tab takes longer
	  than the throttle but far less than a minute.
	*/
	const { data, error } = useSWR(!disable ? ["auth", serverUrl] : null, (_key, url) => auth(url), {
		errorRetryCount: 1,
		revalidateOnFocus: true,
		revalidateOnReconnect: true,
	});

	/*
	  Adopt whatever the auth call reports. Guard on `data` so this only fires
	  after a successful fetch, and not at all when `disable` is true, because
	  SWR is paused and `data` stays undefined.

	  `/api/cosmos/auth` returns the account it just verified, so take it
	  directly. That saves a round trip and, more importantly, keeps the client
	  from showing an account the server has already decided is signed out —
	  which is how the nav came to display a name on a page that rendered
	  logged out (MW-13701). Fall back to a fetch for responses without it.
	*/
	useEffect(() => {
		if (!data) return;
		if (data.account) {
			setAccount(data.account);
			return;
		}
		void refreshAccount();
	}, [data, refreshAccount, setAccount]);

	return {
		auth: data,
		isLoading: !data && !error,
		error,
	};
};
